import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { CostSettingCost, CostSettingProfile } from 'lumen/models';
import type { CostModel } from 'lumen/routes/market/settings/cost';
import type { BookingWindow } from 'lumen/utils/net-revenue/cost/booking-window';
import { buildDefaultBookingWindow } from 'lumen/utils/net-revenue/cost/booking-window';
import type {
  BookingWindowRow,
  CommissionMode,
  CommissionUnit,
  CostValuesView,
} from 'lumen/utils/net-revenue/cost/types';
import type { ControllerWithUnsavedChanges } from 'lumen/utils/unsaved-changes-transition-handlers';

export default class MarketSettingsCostNewController
  extends Controller
  implements ControllerWithUnsavedChanges
{
  declare model: CostModel;

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked bookingWindows: BookingWindow[] = [buildDefaultBookingWindow()];
  // Each unit keeps its own amount, so an accidental toggle is undoable.
  @tracked currencyValue: number | null = null;
  @tracked description = '';
  @tracked hasAttemptedSave = false;
  @tracked mode: CommissionMode = 'simple';
  @tracked name = '';
  @tracked percentageValue: number | null = null;
  @tracked unit: CommissionUnit = 'percentage';

  saveTask = task({ drop: true }, async (): Promise<void> => {
    this.hasAttemptedSave = true;

    // A fast navigation can reach Save before the uniqueness list loads.
    if (this.model.profiles.isRunning) {
      try {
        await this.model.profiles.last;
      } catch {
        return;
      }
    }

    if (!this.model.profiles.last?.isSuccessful) {
      // Abort silently — the cost route already alerted on the failed load.
      return;
    }

    if (!this.isValid) {
      return;
    }

    const costValues: CostValuesView =
      this.mode === 'advanced'
        ? { bookingWindows: this.bookingWindowsForSave, value: null }
        : { bookingWindows: [], value: this.commissionValue };

    const profile = this.store.createRecord<CostSettingProfile>(
      'cost-setting/profile',
      { name: this.name.trim(), description: this.description.trim() || null },
    );

    const cost = this.store.createRecord<CostSettingCost>('cost-setting/cost', {
      profile,
      commissionType: this.mode,
      costValues,
      valueType: this.unit,
    });

    try {
      await profile.save();
    } catch (error) {
      // No response to normalize, so clean up the new records here.
      cost.unloadRecord();
      profile.unloadRecord();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });

      return;
    }

    try {
      await this.model.profiles.perform();
    } catch {
      // Already created, and the cost route alerts on a failed refresh.
    }

    this.toast.success(this.intl.t('settings.cost.created'), {
      showIcon: true,
    });
    this.resetForm();
    this.router.transitionTo('market.settings.cost');
  });

  get bookingWindowRows(): BookingWindowRow[] {
    return this.bookingWindows.map((bookingWindow) => ({
      id: bookingWindow.id,
      label: this.intl.t('settings.cost.advanced.all_default'),
      values: this.activeValues(bookingWindow),
    }));
  }

  get bookingWindowsForSave(): CostValuesView['bookingWindows'] {
    return this.bookingWindows.map((bookingWindow) => ({
      leadtimeEnd: bookingWindow.leadtimeEnd,
      leadtimeStart: bookingWindow.leadtimeStart,
      values: this.activeValues(bookingWindow),
    }));
  }

  get commissionValue(): number | null {
    return this.unit === 'currency' ? this.currencyValue : this.percentageValue;
  }

  get costError(): string | undefined {
    if (!this.hasAttemptedSave) {
      return undefined;
    }

    if (this.mode === 'advanced') {
      return this.isAdvancedMatrixComplete
        ? undefined
        : this.intl.t('settings.cost.error.matrix_required');
    }

    return this.commissionValue === null
      ? this.intl.t('settings.cost.error.cost_required')
      : undefined;
  }

  get existingNames(): Set<string> {
    const profiles = this.model.profiles.last?.value ?? [];

    return new Set(
      profiles.map((profile) => (profile.name ?? '').trim().toLowerCase()),
    );
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.name.trim().length > 0 ||
      this.description.trim().length > 0 ||
      this.currencyValue !== null ||
      this.percentageValue !== null ||
      this.bookingWindows.some(
        (bookingWindow) =>
          bookingWindow.currencyValues.some((value) => value !== null) ||
          bookingWindow.percentageValues.some((value) => value !== null),
      )
    );
  }

  // Only the active unit's cells count — the other unit can stay empty.
  get isAdvancedMatrixComplete(): boolean {
    return this.bookingWindows.every((bookingWindow) =>
      this.activeValues(bookingWindow).every((value) => value !== null),
    );
  }

  get isNameUnique(): boolean {
    return !this.existingNames.has(this.name.trim().toLowerCase());
  }

  get isSaveDisabled(): boolean {
    return (
      this.saveTask.isRunning ||
      this.nameError !== undefined ||
      this.costError !== undefined
    );
  }

  get isValid(): boolean {
    if (this.name.trim().length === 0 || !this.isNameUnique) {
      return false;
    }

    return this.mode === 'advanced'
      ? this.isAdvancedMatrixComplete
      : this.commissionValue !== null;
  }

  get nameError(): string | undefined {
    if (!this.hasAttemptedSave) {
      return undefined;
    }

    if (this.name.trim().length === 0) {
      return this.intl.t('settings.cost.name.error.required');
    }

    if (!this.isNameUnique) {
      return this.intl.t('settings.cost.name.error.not_unique');
    }

    return undefined;
  }

  activeValues(bookingWindow: BookingWindow): (number | null)[] {
    return this.unit === 'currency'
      ? bookingWindow.currencyValues
      : bookingWindow.percentageValues;
  }

  resetForm(): void {
    this.bookingWindows = [buildDefaultBookingWindow()];
    this.currencyValue = null;
    this.description = '';
    this.hasAttemptedSave = false;
    this.mode = 'simple';
    this.name = '';
    this.percentageValue = null;
    this.unit = 'percentage';
  }

  @action discardChanges(): void {
    this.resetForm();
  }

  @action showDiscardChangesAlert(): Promise<boolean> {
    return this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: { text: this.intl.t('general.keep_editing') },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });
  }

  @action updateAdvancedCell(
    bookingWindowId: string,
    dayIndex: number,
    value: number | null,
  ): void {
    const bookingWindow = this.bookingWindows.find(
      (candidate) => candidate.id === bookingWindowId,
    );
    if (!bookingWindow) {
      return;
    }

    const values = this.activeValues(bookingWindow);
    if (values[dayIndex] === value) {
      return;
    }

    const next = [...values];
    next[dayIndex] = value;
    if (this.unit === 'currency') {
      bookingWindow.currencyValues = next;

      return;
    }

    bookingWindow.percentageValues = next;
  }

  @action updateCommissionValue(value: number | null): void {
    if (this.unit === 'currency') {
      this.currencyValue = value;

      return;
    }

    this.percentageValue = value;
  }

  @action updateDescription(event: Event): void {
    this.description = (event.target as HTMLInputElement).value;
  }

  @action updateMode(mode: CommissionMode): void {
    this.mode = mode;
  }

  @action updateName(event: Event): void {
    this.name = (event.target as HTMLInputElement).value;
  }

  @action updateUnit(unit: CommissionUnit): void {
    this.unit = unit;
  }
}
